// Embedded AHU list (Phase 3b) pure helpers — derived from
// PHASE3_PROJECTBOARD_INVENTORY.md Part B §B1 and the live Backbone source
// (ahus/view/ahus.js, /static/mvc/new_ahu/view/new_ahu.js,
// project/model/project_ahu_unit.js, universal_fn_tools.js).

import type { IAhuUnit } from 'models';

/** One rendered row of the mains+versions tree, in render (data-index) order. */
export interface AhuTreeRow {
  unit: IAhuUnit;
  /** Global render index (`project_{{index}}` / data-index — keyboard nav). */
  index: number;
  isVersion: boolean;
}

/** One main group: the main row + its (version_order, id)-sorted versions. */
export interface AhuMainGroup {
  main: AhuTreeRow;
  versions: AhuTreeRow[];
}

/**
 * ahuUnitsRender tree build (B1.3 / parity 131): mains =
 * `parent_version_id == null` in collection order; versions grouped under
 * their parent, sorted by version_order (null → 0) then id. Orphan versions
 * (parent not in the list) are dropped — the legacy renders them nowhere.
 */
export function buildAhuTree(units: IAhuUnit[]): AhuMainGroup[] {
  const mains: IAhuUnit[] = [];
  const versionsByParent = new Map<number, IAhuUnit[]>();
  units.forEach((u) => {
    if (u.parent_version_id) {
      const list = versionsByParent.get(u.parent_version_id) ?? [];
      list.push(u);
      versionsByParent.set(u.parent_version_id, list);
    } else {
      mains.push(u);
    }
  });
  versionsByParent.forEach((list) => {
    list.sort((a, b) => {
      const ao = a.version_order == null ? 0 : a.version_order;
      const bo = b.version_order == null ? 0 : b.version_order;
      if (ao !== bo) return ao - bo;
      return a.id - b.id;
    });
  });

  let globalIndex = 0;
  return mains.map((main) => {
    const mainRow: AhuTreeRow = { unit: main, index: globalIndex++, isVersion: false };
    const versions = (versionsByParent.get(main.id) ?? []).map(
      (v): AhuTreeRow => ({ unit: v, index: globalIndex++, isVersion: true })
    );
    return { main: mainRow, versions };
  });
}

/** Flat render-order row list (keyboard ↑/↓ moves by this data-index order). */
export function flattenAhuTree(groups: AhuMainGroup[]): AhuTreeRow[] {
  const rows: AhuTreeRow[] = [];
  groups.forEach((g) => {
    rows.push(g.main);
    rows.push(...g.versions);
  });
  return rows;
}

/** canShowRemoveOption (B1.3): unfinished but persisted (created_on set). */
export const canShowRemoveOption = (u: IAhuUnit): boolean =>
  !!(u && !u.finished && u.created_on);

/** Row `.non-clickable` state: never finished AND no created_on (B1.3). */
export const isAhuRowDisabled = (u: IAhuUnit): boolean =>
  !u.finished && !canShowRemoveOption(u);

/** Row display tag: static_version_name for static versions, else tag. */
export const ahuDisplayTag = (u: IAhuUnit): string =>
  (u.static_version && u.static_version_name ? u.static_version_name : u.tag) ?? '';

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

/**
 * New-AHU default tag `AH-{yy}{mm}{dd}_{hh}{mm}{ss}` (new_ahu.js ahuNewName).
 */
export function buildNewAhuDefaultTag(now: Date = new Date()): string {
  const year = String(now.getFullYear()).slice(-2);
  return (
    `AH-${year}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}` +
    `_${pad2(now.getHours())}${pad2(now.getMinutes())}${pad2(now.getSeconds())}`
  );
}

/**
 * Duplicate/version default tag `{sourceTag truncated}_{yyyymmdd}_{hhmmss}`
 * capped at 30 chars total (ahus.js _buildDefaultTag).
 */
export function buildDuplicateDefaultTag(
  sourceTag: string,
  now: Date = new Date()
): string {
  const suffix =
    `_${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}` +
    `_${pad2(now.getHours())}${pad2(now.getMinutes())}${pad2(now.getSeconds())}`;
  const maxLen = 30;
  const head = (sourceTag || '').slice(0, Math.max(0, maxLen - suffix.length));
  return head + suffix;
}

/**
 * fn.uni_tools.formatNumberWithCommaSeparator — comma thousands grouping with
 * the legacy passthrough quirks (non-numeric returned as-is; numbers under
 * 1000 keep their original string form).
 */
export function formatNumberWithCommaSeparator(
  value: number | string | null | undefined
): string {
  if (value == null) return '';
  if (Number.isNaN(Number(value))) return String(value);
  const grouped = Number(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return grouped.includes(',') ? grouped : String(value);
}

/** New-AHU tag validity (new_ahu.tmpl pattern + minlength 3 / maxlength 30).
 *  dev-sync F (#823 fix-ahu-tag-validation): dev RELAXED this from the old
 *  restrictive character class to "any 3-30 characters"
 *  (new_ahu.tmpl:10 `pattern=".{3,30}"`), so tags containing characters the
 *  old class rejected (e.g. `/`, `#`, `"`) are now accepted. The project-name
 *  pattern was deliberately NOT relaxed by dev and is left alone. */
export const AHU_TAG_PATTERN = '.{3,30}';
